document.documentElement.classList.add('js');

const applyThemeFavicon = (root = document) => {
  const source = root.querySelector('[data-theme-favicon]');
  if (!source?.dataset.themeFavicon) return;
  let favicon = document.head.querySelector('link[data-theme-favicon-link]');
  if (!favicon) {
    favicon = document.createElement('link');
    favicon.rel = 'icon';
    favicon.dataset.themeFaviconLink = '';
    document.head.appendChild(favicon);
  }
  favicon.href = source.dataset.themeFavicon;
};

applyThemeFavicon();

document.addEventListener('shopify:section:load', (event) => {
  applyThemeFavicon(event.target);
});

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-menu-toggle]');
  if (toggle) {
    const nav = document.querySelector('[data-mobile-nav]');
    const open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!open));
    nav.hidden = open;
    document.body.classList.toggle('menu-open', !open);
  }
});

document.addEventListener('change', (event) => {
  if (!event.target.matches('[data-variant-select]')) return;
  const option = event.target.selectedOptions[0];
  const form = event.target.closest('form');
  const button = form.querySelector('[type="submit"]');
  const price = document.querySelector('[data-product-price]');
  const comparePrice = document.querySelector('[data-product-compare-price]');
  const hasSalePrice = option.dataset.onSale === 'true' && option.dataset.comparePrice && option.dataset.comparePrice !== option.dataset.price;
  if (price && option.dataset.price) price.textContent = option.dataset.price;
  if (comparePrice) {
    comparePrice.hidden = !hasSalePrice;
    if (hasSalePrice) comparePrice.textContent = option.dataset.comparePrice;
  }
  button.disabled = option.disabled;
  button.textContent = option.disabled ? 'Sold out' : 'Add to cart';
});

let cartTrigger = null;

const getCartRoot = () => document.querySelector('[data-cart-drawer-shell]')?.dataset.rootUrl || '/';

const openCartDrawer = () => {
  const shell = document.querySelector('[data-cart-drawer-shell]');
  const drawer = shell?.querySelector('.cart-drawer');
  if (!shell || !drawer) return;
  cartTrigger = document.activeElement;
  shell.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');
  document.body.classList.add('cart-drawer-open');
  shell.querySelector('.cart-drawer__close')?.focus();
};

const closeCartDrawer = () => {
  const shell = document.querySelector('[data-cart-drawer-shell]');
  const drawer = shell?.querySelector('.cart-drawer');
  shell?.classList.remove('is-open');
  drawer?.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('cart-drawer-open');
  cartTrigger?.focus?.();
};

const updateCartCounts = (count) => {
  document.querySelectorAll('.cart-count').forEach((badge) => {
    badge.textContent = count;
  });
};

const refreshCartDrawer = async (shouldOpen = true) => {
  const root = getCartRoot();
  const response = await fetch(`${root}?section_id=cart-drawer`, { headers: { Accept: 'text/html' } });
  if (!response.ok) throw new Error('Unable to refresh cart');
  const html = await response.text();
  const documentFragment = new DOMParser().parseFromString(html, 'text/html');
  const replacement = documentFragment.querySelector('[data-cart-drawer-shell]');
  const current = document.querySelector('[data-cart-drawer-shell]');
  if (!replacement || !current) throw new Error('Cart drawer markup unavailable');
  current.replaceWith(replacement);
  updateCartCounts(replacement.dataset.cartCount || '0');
  if (shouldOpen) openCartDrawer();
};

document.addEventListener('click', async (event) => {
  const openButton = event.target.closest('[data-cart-open]');
  if (openButton) {
    event.preventDefault();
    openCartDrawer();
    return;
  }

  if (event.target.closest('[data-cart-close]')) {
    closeCartDrawer();
    return;
  }

  const changeButton = event.target.closest('[data-cart-change]');
  if (!changeButton) return;
  changeButton.disabled = true;
  try {
    const response = await fetch(`${getCartRoot()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line: Number(changeButton.dataset.line),
        quantity: Number(changeButton.dataset.quantity)
      })
    });
    if (!response.ok) throw new Error('Unable to update cart');
    await refreshCartDrawer(true);
  } catch (error) {
    window.location.href = `${getCartRoot()}cart`;
  }
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form.product-form, form.quick-add-form');
  if (!form) return;
  event.preventDefault();
  const button = form.querySelector('[type="submit"]');
  const errorMessage = form.querySelector('[data-product-form-error]');
  if (errorMessage) {
    errorMessage.hidden = true;
    errorMessage.textContent = '';
  }
  button.disabled = true;
  const originalText = button.textContent;
  button.textContent = 'Adding…';
  try {
    const response = await fetch(`${getCartRoot()}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this item.');
    await refreshCartDrawer(true);
    button.textContent = 'Added';
    window.setTimeout(() => {
      button.disabled = false;
      button.textContent = originalText;
    }, 900);
  } catch (error) {
    button.disabled = false;
    button.textContent = originalText;
    if (errorMessage) {
      errorMessage.textContent = error.message;
      errorMessage.hidden = false;
    }
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && document.body.classList.contains('cart-drawer-open')) closeCartDrawer();
});

const reveal = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      entry.target.classList.add('is-visible');
      reveal.unobserve(entry.target);
    }
  });
}, { threshold: .12 });

document.querySelectorAll('[data-reveal]').forEach((el) => reveal.observe(el));

const initializeProductGallery = (gallery) => {
  if (gallery.dataset.galleryInitialized === 'true') return;

  const mainImage = gallery.querySelector('[data-gallery-main]');
  const thumbnails = Array.from(gallery.querySelectorAll('[data-gallery-thumbnail]'));
  const dialog = gallery.querySelector('[data-gallery-dialog]');
  const dialogImage = gallery.querySelector('[data-gallery-dialog-image]');
  let selectedIndex = 0;

  if (!mainImage) return;
  gallery.dataset.galleryInitialized = 'true';

  const selectImage = (index) => {
    if (!thumbnails.length) return;
    selectedIndex = (index + thumbnails.length) % thumbnails.length;
    const thumbnail = thumbnails[selectedIndex];
    mainImage.src = thumbnail.dataset.galleryImage;
    mainImage.removeAttribute('srcset');
    mainImage.alt = thumbnail.dataset.galleryAlt;
    thumbnails.forEach((item, itemIndex) => {
      const active = itemIndex === selectedIndex;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-current', String(active));
    });
    thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  };

  thumbnails.forEach((thumbnail, index) => {
    thumbnail.addEventListener('click', () => selectImage(index));
  });

  gallery.querySelector('[data-gallery-previous]')?.addEventListener('click', () => selectImage(selectedIndex - 1));
  gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => selectImage(selectedIndex + 1));

  gallery.querySelector('[data-gallery-open]')?.addEventListener('click', () => {
    if (!dialog || !dialogImage) return;
    dialogImage.src = mainImage.currentSrc || mainImage.src;
    dialogImage.alt = mainImage.alt;
    dialog.showModal();
  });

  gallery.querySelector('[data-gallery-close]')?.addEventListener('click', () => dialog?.close());
  dialog?.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  gallery.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') selectImage(selectedIndex - 1);
    if (event.key === 'ArrowRight') selectImage(selectedIndex + 1);
  });
};

const initializeProductGalleries = (root = document) => {
  root.querySelectorAll('[data-product-gallery]').forEach(initializeProductGallery);
};

initializeProductGalleries();

document.addEventListener('shopify:section:load', (event) => {
  initializeProductGalleries(event.target);
});

let quickSearchTimer;
let quickSearchRequest;

const closeHeaderPopovers = (exception = null) => {
  document.querySelectorAll('[data-search-panel], [data-mega-panel]').forEach((panel) => {
    if (panel === exception) return;
    panel.hidden = true;
    const trigger = panel.closest('[data-quick-search], [data-mega-menu]')?.querySelector('[aria-expanded]');
    trigger?.setAttribute('aria-expanded', 'false');
  });
};

const renderQuickSearchResults = (root, products, query) => {
  const results = root.querySelector('[data-search-results]');
  if (!results) return;
  results.replaceChildren();

  if (!products.length) {
    const message = document.createElement('p');
    message.className = 'quick-search__message';
    message.textContent = `No products found for “${query}”.`;
    results.appendChild(message);
    return;
  }

  products.slice(0, 3).forEach((product) => {
    const item = document.createElement('a');
    item.className = 'quick-search__item';
    item.href = product.url;

    const image = document.createElement('img');
    image.src = product.featured_image?.url || product.image || '';
    image.alt = '';
    image.loading = 'lazy';

    const title = document.createElement('strong');
    title.textContent = product.title;

    item.append(image, title);
    results.appendChild(item);
  });

  const allResults = document.createElement('a');
  allResults.className = 'quick-search__all';
  allResults.href = `/search?q=${encodeURIComponent(query)}&type=product`;
  allResults.textContent = 'View all results →';
  results.appendChild(allResults);
};

document.addEventListener('click', (event) => {
  if (event.target.closest('[data-search-close]') || event.target.matches('[data-search-panel]')) {
    closeHeaderPopovers();
    return;
  }

  const searchToggle = event.target.closest('[data-search-toggle]');
  if (searchToggle) {
    const root = searchToggle.closest('[data-quick-search]');
    const panel = root?.querySelector('[data-search-panel]');
    if (!panel) return;
    const opening = panel.hidden;
    closeHeaderPopovers(opening ? panel : null);
    panel.hidden = !opening;
    searchToggle.setAttribute('aria-expanded', String(opening));
    if (opening) root.querySelector('[data-search-input]')?.focus();
    return;
  }

  const megaTrigger = event.target.closest('[data-mega-trigger]');
  if (megaTrigger) {
    const panel = megaTrigger.closest('[data-mega-menu]')?.querySelector('[data-mega-panel]');
    if (!panel) return;
    const opening = panel.hidden;
    closeHeaderPopovers(opening ? panel : null);
    panel.hidden = !opening;
    megaTrigger.setAttribute('aria-expanded', String(opening));
    return;
  }

  if (!event.target.closest('[data-quick-search], [data-mega-menu]')) closeHeaderPopovers();
});

document.addEventListener('input', (event) => {
  if (!event.target.matches('[data-search-input]')) return;
  const input = event.target;
  const root = input.closest('[data-quick-search]');
  const query = input.value.trim();
  window.clearTimeout(quickSearchTimer);
  quickSearchRequest?.abort();

  if (query.length < 2) {
    root.querySelector('[data-search-results]')?.replaceChildren();
    return;
  }

  quickSearchTimer = window.setTimeout(async () => {
    quickSearchRequest = new AbortController();
    try {
      const searchableFields = 'title,product_type,variants.title,vendor,variants.sku,variants.barcode,tag';
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=3&resources[options][unavailable_products]=hide&resources[options][fields]=${encodeURIComponent(searchableFields)}`, {
        signal: quickSearchRequest.signal,
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Search unavailable');
      const data = await response.json();
      renderQuickSearchResults(root, data.resources?.results?.products || [], query);
    } catch (error) {
      if (error.name === 'AbortError') return;
      renderQuickSearchResults(root, [], query);
    }
  }, 220);
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeHeaderPopovers();
});
